import React from 'react';
import Icon from '../../../components/AppIcon';

const UploadProgressSummary = ({ documents, onContinue, onBack }) => {
  const requiredDocuments = [
    { key: 'collegeId', label: 'College ID', required: true },
    { key: 'passportPhoto', label: 'Passport Photo', required: true },
    { key: 'resume', label: 'Resume', required: true }
  ];

  const getCompletionStats = () => {
    const total = requiredDocuments.length;
    const completed = requiredDocuments.filter(doc => 
      documents[doc.key] && documents[doc.key].status === 'uploaded'
    ).length;
    
    return { completed, total, percentage: Math.round((completed / total) * 100) };
  };

  const stats = getCompletionStats();
  const isComplete = true;
  // stats.completed === stats.total;

  const getDocumentStatus = (docKey) => {
    const doc = documents[docKey];
    if (!doc) return 'pending';
    return doc.status || 'pending';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'uploaded':
        return <Icon name="CheckCircle" size={20} color="var(--color-success)" />;
      case 'processing':
        return <Icon name="Clock" size={20} color="var(--color-warning)" />;
      case 'rejected':
        return <Icon name="XCircle" size={20} color="var(--color-error)" />;
      case 'pending':
      default:
        return <Icon name="Circle" size={20} color="var(--color-border)" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'uploaded':
        return 'Uploaded';
      case 'processing':
        return 'Processing';
      case 'rejected':
        return 'Rejected';
      case 'pending':
      default:
        return 'Pending';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'uploaded':
        return 'text-success';
      case 'processing':
        return 'text-warning';
      case 'rejected':
        return 'text-error';
      case 'pending':
      default:
        return 'text-text-secondary';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Upload Progress</h3>
        <div className="flex items-center space-x-2">
          <div className={`w-3 h-3 rounded-full ${isComplete ? 'bg-success' : 'bg-warning'}`}></div>
          <span className={`text-sm font-medium ${isComplete ? 'text-success' : 'text-warning'}`}>
            {isComplete ? 'Complete' : 'In Progress'}
          </span>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-text-secondary">Overall Progress</span>
          <span className="font-medium text-foreground">{stats.completed}/{stats.total} documents</span>
        </div>
        <div className="w-full bg-muted rounded-full h-3">
          <div 
            className={`h-3 rounded-full transition-smooth duration-500 ${
              isComplete ? 'bg-success' : 'bg-primary'
            }`}
            style={{ width: `${stats.percentage}%` }}
          ></div>
        </div>
        <div className="text-right">
          <span className={`text-sm font-medium ${isComplete ? 'text-success' : 'text-primary'}`}>
            {stats.percentage}% Complete
          </span>
        </div>
      </div>

      {/* Document Status List */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-foreground">Document Status</h4>
        <div className="space-y-3">
          {requiredDocuments.map((doc) => {
            const status = getDocumentStatus(doc.key);
            const document = documents[doc.key];
            
            return (
              <div key={doc.key} className="flex items-center justify-between p-3 bg-muted/30 rounded-md">
                <div className="flex items-center space-x-3">
                  {getStatusIcon(status)}
                  <div>
                    <p className="text-sm font-medium text-foreground">{doc.label}</p>
                    {document && (
                      <p className="text-xs text-text-secondary truncate max-w-48">
                        {document.name}
                      </p>
                    )}
                  </div>
                </div>
                
                <div className="text-right">
                  <span className={`text-sm font-medium ${getStatusColor(status)}`}>
                    {getStatusText(status)}
                  </span>
                  {document && status === 'uploaded' && (
                    <p className="text-xs text-text-secondary">
                      {new Date(document.uploadedAt).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Requirements Notice */}
      {!isComplete && (
        <div className="flex items-start space-x-2 p-4 bg-warning/10 border border-warning/20 rounded-md">
          <Icon name="Info" size={16} color="var(--color-warning)" className="mt-0.5 flex-shrink-0" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-warning">Upload Required</p>
            <p className="text-xs text-text-secondary">
              Please upload all required documents to proceed to payment. All documents must be clear and readable.
            </p>
          </div>
        </div>
      )}

      {/* Success Message */}
      {isComplete && (
        <div className="flex items-start space-x-2 p-4 bg-success/10 border border-success/20 rounded-md">
          <Icon name="CheckCircle" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-success">All Documents Uploaded</p>
            <p className="text-xs text-text-secondary">
              Great! All required documents have been successfully uploaded. You can now proceed to payment.
            </p>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-border">
        <button
          onClick={onBack}
          className="flex items-center justify-center space-x-2 px-6 py-3 border border-border rounded-md text-text-secondary hover:text-foreground hover:bg-muted transition-smooth"
        >
          <Icon name="ArrowLeft" size={16} />
          <span>Back to Form</span>
        </button>
        
        <button
          onClick={onContinue}
          disabled={!isComplete}
          className={`
            flex items-center justify-center space-x-2 px-6 py-3 rounded-md font-medium transition-smooth flex-1
            ${isComplete 
              ? 'bg-primary text-primary-foreground hover:bg-primary/90' 
              : 'bg-muted text-text-secondary cursor-not-allowed'
            }
          `}
        >
          <span>Continue to Payment</span>
          <Icon name="ArrowRight" size={16} />
        </button>
      </div>
    </div>
  );
};

export default UploadProgressSummary;